import type { SignupOnboardingState } from "@/lib/onboarding/signup-onboarding"

export type SignupOnboardingPersonalizationSource =
  | "website"
  | "documents"
  | "social"
  | "shopify"

/** A source still being chosen reads as website. */
export function resolveSignupOnboardingPersonalizationSource(
  state: Pick<SignupOnboardingState, "knowledge_source">,
): SignupOnboardingPersonalizationSource {
  switch (state.knowledge_source) {
    case "documents":
      return "documents"
    case "shopify":
      return "shopify"
    case "instagram":
    case "facebook":
      return "social"
    case "website":
    case "not_selected":
    case "skipped":
      return "website"
    default: {
      const exhaustiveSource: never = state.knowledge_source
      return exhaustiveSource
    }
  }
}

/** Only an import makes the step wait; documents and a store are read on demand. */
export function isSignupOnboardingPersonalizationReady({
  source,
  hasOriginalPrompt,
  importJobId,
  importJobStatus,
  readyWebsitePages,
}: {
  source: SignupOnboardingPersonalizationSource
  hasOriginalPrompt: boolean
  importJobId: string | null
  importJobStatus: string | null
  readyWebsitePages: number
}): boolean {
  if (!hasOriginalPrompt) return false

  switch (source) {
    case "documents":
    case "shopify":
      return true
    case "social":
      return Boolean(
        importJobId &&
          importJobStatus &&
          TERMINAL_IMPORT_JOB_STATUSES.includes(importJobStatus),
      )
    case "website":
      return Boolean(
        importJobId &&
          importJobStatus &&
          (readyWebsitePages >= WEBSITE_PAGES_BEFORE_PERSONALIZATION ||
            TERMINAL_IMPORT_JOB_STATUSES.includes(importJobStatus)),
      )
    default: {
      const exhaustiveSource: never = source
      return exhaustiveSource
    }
  }
}

export const WEBSITE_PAGES_BEFORE_PERSONALIZATION = 10

const TERMINAL_IMPORT_JOB_STATUSES = ["completed", "failed", "cancelled"]
